<template>
  <!-- Display Sub-itinerary information for Itinerary path -->
  <!-- Component is also used for Routes -->
  <div id="sub-itinerary-container">
    <div
      id="sub-itinerary-description-container"
      v-if="current.subItineraryDescription && current.subItineraryDescription.length > 0"
    >
      <div
        id="description-language-selection"
        class="language-selection"
        v-if="descriptionLanguageOptions.length > 1"
      >
        <label>Read in </label>
        <select v-model="descriptionLanguage">
          <option
            v-for="language in descriptionLanguageOptions"
            :key="language"
          >
            {{ language }}
          </option>
        </select>
      </div>
      <DescriptionComponent
        v-if="current.subItineraryDescription && current.subItineraryDescription.length > 0"
        :language="descriptionLanguage"
        :rtl="checkRTL(descriptionLanguage)"
        :texts="
          current.subItineraryDescription.length > 0
            ? current.subItineraryDescription[0].details
            : []
        "
        :characters="descriptionChars"
        :glossary="current.glossary"
      >
      </DescriptionComponent>
      <div
        v-if="
          current.subItineraryDescription && 
          current.subItineraryDescription.length > 0 &&
          current.subItineraryDescription[0] && 
          descriptionLanguage && 
          returnValueInLanguage(
            descriptionLanguage,
            current.subItineraryDescription[0].details
          ).author.length > 0
        "
        id="sub-itinerary-description-author"
      >
        Author:
        {{
          returnValueInLanguage(
            descriptionLanguage,
            current.subItineraryDescription[0].details
          ).author
        }}
      </div>
    </div>

    <!-- More than one description is possible -->
    <div
      id="alt-description-container"
      v-if="altDescriptionLanguage.length > 0"
    >
      <div
        id="alt-description-language-selection"
        class="language-selection"
        v-if="displayAltDescription"
      >
        <label>Read in </label>
        <select v-model="altDescriptionLanguage">
          <option
            v-for="language in altDescriptionLanguageOptions"
            :key="language"
          >
            {{ language }}
          </option>
        </select>
      </div>
      <div
        id="alt-description"
        v-if="displayAltDescription"
        v-html="
          returnValueInLanguage(
            altDescriptionLanguage,
            current.subItineraryDescription[1].details
          ).description
        "
      ></div>
      <a
        class="alt-description-link expand"
        v-if="!displayAltDescription"
        @click="displayAltDescription = !displayAltDescription"
      >
        Read detailed Description.
      </a>
      <a
        class="alt-description-link collapse"
        v-else
        @click="displayAltDescription = !displayAltDescription"
      >
        Hide detailed Description.
      </a>
    </div>

    <div id="sub-itinerary-list-label">
      <span v-if="isSubItinerary"
        >Locations and Monuments inside this Sub-Itinerary:</span
      >
      <span v-else>Monuments inside this Route:</span>
    </div>

    <!-- Location tabs, one per Location -->
    <!-- Selection changes the side navigation, breadcrumbs, and page content -->
    <div
      id="location-tabs-container"
      ref="locationTabsContainer"
      v-if="isSubItinerary"
    >
      <div
        id="location-arrows-container"
        ref="locationArrowsContainer"
        v-if="next && next.length > 5 && !lessThan899"
      >
        <button
          ref="locationLeftArrow"
          class="location-arrow-button"
          @click="scrollRight()"
        >
          <font-awesome-icon
            :icon="['fas', 'chevron-left']"
            class="location-arrow"
          />
        </button>
        <button
          ref="locationRightArrow"
          class="location-arrow-button"
          @click="scrollLeft()"
        >
          <font-awesome-icon
            :icon="['fas', 'chevron-right']"
            class="location-arrow"
          />
        </button>
      </div>
      <div
        id="location-tabs"
        ref="locationTabs"
        :class="{
          'start-tabs': next.length > 5,
          'center-tabs': next.length <= 5,
        }"
      >
        <div
          class="location-tab-wrapper"
          v-for="(entry, index) in next"
          :key="entry.thisId"
        >
          <div
            class="location-tab"
            :class="{ selected: locationIndex === index }"
            @click="setAndEmitIndex(index)"
            v-if="
              !lessThan899 ||
              (lessThan899 && view[index]) ||
              (lessThan899 && showTabDropdowns)
            "
          >
            {{ entry.name.toUpperCase() }}
            <font-awesome-icon
              :icon="['fas', 'chevron-down']"
              class="location-tab-arrow"
              v-if="
                lessThan899 &&
                !showTabDropdowns &&
                view[index] &&
                next.length > 1
              "
            />
            <font-awesome-icon
              :icon="['fas', 'chevron-up']"
              class="location-tab-arrow"
              v-else-if="
                lessThan899 &&
                showTabDropdowns &&
                view[index] &&
                next.length > 1
              "
            />
          </div>
        </div>
      </div>
    </div>
    <div id="explore-map-container">
      <div id="explore-map" ref="exploreMap"></div>
    </div>
    <div
      class="location-info-container"
      v-for="(entry, index) in next"
      :key="`${entry.thisId}-info`"
    >
      <div v-if="locationIndex === index">
        <!-- Location description text -->
        <div class="location-background-container">
          <div
            class="location-background-label"
            v-if="
              entry.locationHistoricalBackground && 
              entry.locationHistoricalBackground.length > 0 &&
              entry.locationHistoricalBackground[0].details &&
              entry.locationHistoricalBackground[0].details.description &&
              returnValueInLanguage(
                locationBackgroundLanguage,
                entry.locationHistoricalBackground[0].details
              ).description.length > 0
            "
          >
            {{ next[locationIndex].name }} | Historical Background:
          </div>
          <div
            id="location-background-language-selection"
            class="language-selection"
            v-if="locationBackgroundLanguageOptions.length > 1"
          >
            <label>Read in </label>
            <select v-model="locationBackgroundLanguage">
              <option
                v-for="language in locationBackgroundLanguageOptions"
                :key="language"
              >
                {{ language }}
              </option>
            </select>
          </div>
          <DescriptionComponent
            v-if="locationBackgroundLanguage.length > 0"
            :language="locationBackgroundLanguage"
            :rtl="checkRTL(locationBackgroundLanguage)"
            :texts="entry.locationHistoricalBackground[0].details"
            :characters="descriptionChars"
          >
          </DescriptionComponent>
        </div>
        <!-- More than one Location description text can be entered -->
        <div
          id="alt-location-background-container"
          v-if="altLocationBackgroundLanguage.length > 0"
        >
          <div
            id="alt-location-background-language-selection"
            class="language-selection"
            v-if="displayAltBackground"
          >
            <label>Read in </label>
            <select v-model="altLocationBackgroundLanguage">
              <option
                v-for="language in altLocationBackgroundLanguageOptions"
                :key="language"
              >
                {{ language }}
              </option>
            </select>
          </div>
          <div
            id="alt-location-background"
            v-if="displayAltBackground"
            v-html="
              returnValueInLanguage(
                altLocationBackgroundLanguage,
                entry.locationHistoricalBackground[1].details
              ).description
            "
          ></div>
          <a
            class="alt-location-background-link expand"
            v-if="!displayAltBackground"
            @click="displayAltBackground = !displayAltBackground"
          >
            Read detailed Historical Background.
          </a>
          <a
            class="alt-location-background-link collapse"
            v-else
            @click="displayAltBackground = !displayAltBackground"
          >
            Hide detailed Historical Background.
          </a>
        </div>
        <!-- List of Monuments within selected Location -->
        <!-- Links for Routes vs. Sub-Itineraries are different (two versions) -->
        <div class="monument-list-container" v-if="entry.monuments">
          <div
            class="monument-tile"
            v-for="monument in monumentsOrdered(entry)"
            :key="monument.monumentId"
            :style="
              monument.monumentImages && monument.monumentImages.length > 0
                ? {
                    backgroundImage:
                      'url(' + monument.monumentImages[0].url + ')',
                  }
                : { backgroundColor: '#006950' }
            "
          >
            <router-link
              v-if="!$route.params.route"
              class="monument-link"
              :to="{
                name: 'itineraries',
                params: {
                  country: current.country
                    ? `c-${current.country}`
                    : $route.params.country
                    ? $route.params.country
                    : current.countryId,
                  territory: current.territory
                    ? `tr-${current.territory}`
                    : $route.params.territory
                    ? $route.params.territory
                    : current.territoryId,
                  itinerary: $route.params.itinerary,
                  subItinerary: $route.params.subItinerary,
                  monument: `m-${monument.monumentId}`,
                },
              }"
            >
            </router-link>
            <router-link
              v-else
              class="monument-link"
              :to="{
                name: 'countries',
                params: {
                  country: $route.params.country,
                  territory: $route.params.territory
                    ? $route.params.territory
                    : undefined,
                  location: $route.params.location,
                  monument: `m-${monument.monumentId}`,
                },
              }"
            >
            </router-link>
            <div class="monument-name">
              <router-link
                v-if="!$route.params.route"
                :to="{
                  name: 'itineraries',
                  params: {
                    country: current.country
                      ? `c-${current.country}`
                      : $route.params.country
                      ? $route.params.country
                      : current.countryId,
                    territory: current.territory
                      ? `tr-${current.territory}`
                      : $route.params.territory
                      ? $route.params.territory
                      : current.territoryId,
                    itinerary: $route.params.itinerary,
                    subItinerary: $route.params.subItinerary,
                    monument: `m-${monument.monumentId}`,
                  },
                }"
              >
                {{
                  truncate(
                    65,
                    returnValueInLanguage("en", monument.monumentName).value
                  )
                }}
              </router-link>
              <router-link
                v-else
                class="monument-link"
                :to="{
                  name: 'countries',
                  params: {
                    country: $route.params.country,
                    territory: $route.params.territory
                      ? $route.params.territory
                      : undefined,
                    location: $route.params.location,
                    monument: `m-${monument.monumentId}`,
                  },
                }"
              >
                {{
                  truncate(
                    65,
                    returnValueInLanguage("en", monument.monumentName).value
                  )
                }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      id="sub-itinerary-local-team-container"
      v-if="
        current.subItineraryDescription &&
        current.subItineraryDescription.length > 0 &&
        current.subItineraryDescription[0].details[0].localTeam &&
        current.subItineraryDescription[0].details[0].localTeam.length > 0
      "
    >
      <div
        id="sub-itinerary-local-team-toggle"
        @click="showLocalTeam = !showLocalTeam"
      >
        <span v-if="!showLocalTeam">&#62;&#62; Show </span>
        <span v-else>&#60;&#60; Hide </span>
        Local Team
      </div>
      <div
        id="sub-itinerary-local-team"
        v-if="showLocalTeam"
        v-html="current.subItineraryDescription[0].details[0].localTeam"
      ></div>
    </div>

    <BooksTours :books="current.mwnfTravelBook" :tours="current.mwnfTour">
    </BooksTours>
  </div>
</template>

<script>
import DescriptionComponent from "./DescriptionComponent.vue";
import BooksTours from "./BooksTours.vue";
import language from "../mixins/language.js";
import truncate from "../mixins/truncate.js";
import media from "../mixins/media.js";
export default {
  name: "SubItinerary",
  components: {
    DescriptionComponent: DescriptionComponent,
    BooksTours: BooksTours,
  },
  mixins: [language, truncate, media],
  props: ["current", "next", "index", "locationId"],
  watch: {
    // On change/update, reset languages, descriptions, etc.
    current() {
      this.locationIndex = 0;
      if (this.current.subItineraryDescription && this.current.subItineraryDescription.length > 0) {
        if (this.current.subItineraryDescription.length > 1) {
          this.getLanguages(
            this.current.subItineraryDescription[1].details,
            "altDescriptionLanguage",
            "altDescriptionLanguageOptions"
          );
        }
        this.getLanguages(
          this.current.subItineraryDescription[0].details,
          "descriptionLanguage",
          "descriptionLanguageOptions"
        );
      }
      if (!this.isSubItinerary) {
        if (
          this.current.locations[this.locationIndex]
            .locationHistoricalBackground.length > 0
        ) {
          if (
            this.current.locations[this.locationIndex]
              .locationHistoricalBackground.length > 1
          ) {
            this.getLanguages(
              this.current.locations[this.locationIndex]
                .locationHistoricalBackground[1].details,
              "altLocationBackgroundLanguage",
              "altLocationBackgroundLanguageOptions"
            );
          }
          this.getLanguages(
            this.current.locations[this.locationIndex]
              .locationHistoricalBackground[0].details,
            "locationBackgroundLanguage",
            "locationBackgroundLanguageOptions"
          );
        } else {
          this.locationBackgroundLanguage = "";
          this.locationBackgroundLanguageOptions = [];
          this.altLocationBackgroundLanguage = "";
          this.altLocationBackgroundLanguageOptions = [];
        }
      }
    },
    next() {
      if (this.isSubItinerary) {
        if (this.locationIndex) {
          if (
            this.next[this.locationIndex].locationHistoricalBackground.length >
            0
          ) {
            if (
              this.next[this.locationIndex].locationHistoricalBackground
                .length > 1
            ) {
              this.getLanguages(
                this.next[this.locationIndex].locationHistoricalBackground[1]
                  .details,
                "altLocationBackgroundLanguage",
                "altLocationBackgroundLanguageOptions"
              );
            }
            this.getLanguages(
              this.next[this.locationIndex].locationHistoricalBackground[0]
                .details,
              "locationBackgroundLanguage",
              "locationBackgroundLanguageOptions"
            );
          }
        } else {
          this.locationBackgroundLanguage = "";
          this.locationBackgroundLanguageOptions = [];
          this.altLocationBackgroundLanguage = "";
          this.altLocationBackgroundLanguageOptions = [];
        }
        this.view = {};
        // Generate view object with index for each Location
        for (let i = 0; i < this.next.length; i++) {
          this.view[i] = false;
        }
        // Automatically select current Location
        this.view[this.locationIndex] = true;
      }
      for (let i = 0; i < this.next.length; i++) {
        if (this.next[i].locationId == this.locationId) {
          this.setAndEmitIndex(i);
        }
      }
    },
    index() {
      this.setAndEmitIndex(this.index);
      let keys = Object.keys(this.view);
      for (let i = 0; i < keys.length; i++) {
        this.view[keys[i]] = false;
      }
      this.view[this.index] = true;
    },
  },
  data() {
    return {
      isSubItinerary: false,
      descriptionLanguage: "",
      descriptionLanguageOptions: [],
      displayAltDescription: false,
      altDescriptionLanguage: "",
      altDescriptionLanguageOptions: [],
      descriptionChars: 575,
      locationIndex: 0,
      locationBackgroundLanguage: "",
      locationBackgroundLanguageOptions: [],
      displayAltBackground: false,
      altLocationBackgroundLanguage: "",
      altLocationBackgroundLanguageOptions: [],
      showLocalTeam: false,
      view: {},

      lessThan899: window.matchMedia("(max-width: 899px)").matches,
      showTabDropdowns: window.matchMedia("(max-width: 899px)").matches
        ? false
        : true,
    };
  },
  methods: {
    // Sort Monuments alphabetically
    monumentsOrdered: function (location) {
      // Shallow copy to avoid render loop message
      return [...location.monuments].sort((a, b) => {
        let orderA = a.monumentOrder;
        let orderB = b.monumentOrder;
        return orderA < orderB ? -1 : orderA > orderB ? 1 : 0;
      });
    },
    setAndEmitIndex: function (index) {
      if (index == this.locationIndex) {
        if (this.lessThan899 && index == this.locationIndex) {
          this.showTabDropdowns = !this.showTabDropdowns;
        }
      } else {
        this.locationIndex = index;
        if (this.next[index].locationHistoricalBackground.length > 0) {
          if (this.next[index].locationHistoricalBackground.length > 1) {
            this.getLanguages(
              this.next[index].locationHistoricalBackground[1].details,
              "altLocationBackgroundLanguage",
              "altLocationBackgroundLanguageOptions"
            );
          }
          this.getLanguages(
            this.next[index].locationHistoricalBackground[0].details,
            "locationBackgroundLanguage",
            "locationBackgroundLanguageOptions"
          );
        } else {
          this.locationBackgroundLanguage = "";
          this.locationBackgroundLanguageOptions = [];
          this.altLocationBackgroundLanguage = "";
          this.altLocationBackgroundLanguageOptions = [];
        }
        this.$emit("location-change", index);
      }
    },
    scrollLeft: function () {
      let current = this.$refs.locationTabs.scrollLeft;
      this.$refs.locationTabs.scrollTo({
        left: current + 200,
        behavior: "smooth",
      });
    },
    scrollRight: function () {
      let current = this.$refs.locationTabs.scrollLeft;
      this.$refs.locationTabs.scrollTo({
        left: current - 200,
        behavior: "smooth",
      });
    },
    checkType: function () {
      if (this.$route.params.subItinerary) {
        this.isSubItinerary = true;
      } else {
        this.isSubItinerary = false;
      }
    },
    checkRTL: function (language) {
      if (language === "ar") {
        return true;
      } else {
        return false;
      }
    },
  },
  mounted() {
    this.checkType();
    if (this.current.subItineraryDescription && this.current.subItineraryDescription.length > 0) {
      if (this.current.subItineraryDescription.length > 1) {
        this.getLanguages(
          this.current.subItineraryDescription[1].details,
          "altDescriptionLanguage",
          "altDescriptionLanguageOptions"
        );
      }
      this.getLanguages(
        this.current.subItineraryDescription[0].details,
        "descriptionLanguage",
        "descriptionLanguageOptions"
      );
    }
  },
};
</script>

<style scoped lang="scss">
#sub-itinerary-container {
  width: 100%;
}

#sub-itinerary-counts {
  @include texts;
  @include font-size-6;
  margin-bottom: 20px;
}

.language-selection {
  margin-bottom: 10px;
}
.language-selection * {
  @include texts;
  @include font-size-6;
}

#sub-itinerary-description-container {
  margin-bottom: 30px;
}
#sub-itinerary-description-author {
  @include texts;
  @include font-size-6;
  margin-top: 10px;
}

.description-toggle {
  cursor: pointer;
  margin: 20px 0;
}
.description-toggle:hover {
  text-decoration: underline;
}

#alt-description-container,
#alt-location-background-container {
  margin-bottom: 30px;
}
#alt-description-container :deep(*),
#alt-location-background-container :deep(*) {
  @include texts;
}
#alt-description-language-selection {
  margin-bottom: 20px;
}
#alt-location-background-language-selection {
  margin-top: 20px;
}
#alt-description-container a,
#alt-location-background-container a {
  display: inline-block;
  font-weight: bold;
}
#alt-location-background-container a {
  margin-top: 20px;
}

#sub-itinerary-list-label {
  @include headings;
  @include font-size-3;
  margin-bottom: 30px;
}

#location-tabs-container {
  display: flex;
  position: relative;
  min-height: 50px;
  width: 100%;
  border-bottom: 1px solid $medium-light-grey;
  padding: 0 30px;
  margin-bottom: 20px;
}
#location-arrows-container {
  @include flex-center;
  justify-content: space-between;
  position: absolute;
  left: 0;
  height: 100%;
  width: 100%;
}
.location-arrow-button {
  @include button-styles;
  height: 100%;
  width: 28px;
  color: $white;
  background-color: $medium-dark-grey;
  z-index: 1;
}
#location-tabs {
  display: flex;
  align-items: center;
  height: 100%;
  width: 100%;
  overflow: hidden;
  z-index: 1;
}
.start-tabs {
  justify-content: flex-start;
}
.center-tabs {
  justify-content: center;
}
.location-tab {
  @include flex-center;
  @include headings;
  @include font-size-5;
  min-height: 50px;
  min-width: 150px;
  width: 150px;
  text-align: center;
  color: $medium-light-grey;
  padding-bottom: 12px;
  margin: 5px 5px 0 5px;
  border-bottom: 5px solid transparent;
  cursor: pointer;
  z-index: 0;
}
.selected {
  color: $dark-green;
  border-bottom: 5px solid $dark-green;
}

#explore-map-container {
  width: 100%;
}
#explore-map-header {
  @include headings;
  @include font-size-3;
  margin-top: 40px;
  margin-bottom: 10px;
}
#explore-map {
  height: 500px;
  width: 100%;
  margin-bottom: 40px;
}

.location-description-container {
  margin-bottom: 20px;
}
.location-background-label,
.location-description-label {
  @include texts;
  @include font-size-6;
  font-weight: bold;
  margin-bottom: 20px;
}

.monument-list-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 20px;
  margin-top: 30px;
}
.monument-tile {
  height: 200px;
  width: 100%;
  background-color: $medium-grey;
  background-position: center;
  background-size: cover;
  border-radius: 10px;
  margin-bottom: 50px;
}
.monument-tile a {
  display: inline-block;
  height: 100%;
  width: 100%;
}
.monument-name {
  @include font-size-6;
  font-weight: bold;
  text-align: left;
  padding-top: 5px;
}
.monument-name a {
  color: $dark-green;
}
/* IE-only */
@media only screen and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  .monument-list-container {
    display: flex;
    flex-wrap: wrap;
  }
  .monument-tile {
    width: 32%;
    margin: 5px 5px 50px 5px;
  }
}

#sub-itinerary-local-team-toggle,
#sub-itinerary-local-team-toggle span,
#sub-itinerary-local-team,
#sub-itinerary-local-team :deep(*) {
  @include texts;
}
#sub-itinerary-local-team-toggle,
#sub-itinerary-local-team-toggle span {
  @include font-size-5;
  font-weight: bold;
  text-decoration: underline;
  color: $dark-green;
}
#sub-itinerary-local-team-toggle {
  margin-bottom: 20px;
}
#sub-itinerary-local-team-toggle:hover {
  cursor: pointer;
}
#sub-itinerary-local-team,
#sub-itinerary-local-team :deep(*) {
  @include font-size-6;
}

@media only screen and (max-width: 949px) {
  .monument-list-container {
    grid-template-columns: repeat(2, 1fr);
  }
  /* IE-only */
  @media only screen and (-ms-high-contrast: none),
    (-ms-high-contrast: active) {
    .monument-list-container {
      display: flex;
      flex-wrap: wrap;
    }
    .monument-tile {
      width: 48%;
      margin: 5px 5px 50px 5px;
    }
  }
}

@media only screen and (max-width: 899px) {
  #location-tabs-container {
    height: auto;
    border-bottom: none;
    padding: 0;
  }
  #location-tabs {
    flex-direction: column;
    position: relative;
  }
  .location-tab-wrapper {
    width: 100%;
  }
  .location-tab {
    width: 100%;
    border-bottom: 5px solid $light-grey;
  }
  .selected {
    color: $dark-green;
    border-bottom: 5px solid $dark-green;
  }
  .location-tab-arrow {
    font-size: 75%;
    margin-left: 10px;
  }
}

@media only screen and (max-width: 649px) {
  .monument-tile {
    height: 150px;
  }
  /* IE-only */
  @media only screen and (-ms-high-contrast: none),
    (-ms-high-contrast: active) {
    .monument-tile {
      width: 46%;
      margin: 5px 5px 50px 5px;
    }
  }
}

@media only screen and (max-width: 499px) {
  .monument-list-container {
    display: flex;
    flex-direction: column;
  }
  .monument-tile {
    width: 100%;
  }
}
</style>
